import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Badge } from "../components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar"
import { PageLayout } from "../components/page-layout"
import { useToast } from "../hooks/use-toast"
import { useState, useEffect } from "react"
import { apiService } from "../lib/api"
import { Users, UserPlus, Shield, Mail, Calendar, CheckCircle, XCircle } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "../contexts/auth-context"
import { TranslatedText } from '../components/translated-text'
import { useTranslation } from '../hooks/useTranslation'

interface User {
  id: string
  email: string
  name: string | null
  role: string
  mfaEnabled: boolean
  createdAt: string
  updatedAt: string
  companyRole?: string // Company-specific role
  companyRoles?: Record<string, string> // Roles for each company
}

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showUserModal, setShowUserModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'accountant',
    password: '',
    companyIds: [] as string[]
  })
  const [userCompanies, setUserCompanies] = useState<string[]>([])
  // Store company roles: { companyId: role }
  const [companyRoles, setCompanyRoles] = useState<Record<string, string>>({})
  const { toast } = useToast()
  const { tSync } = useTranslation()
  const queryClient = useQueryClient()
  const { isAuthenticated, isLoading: authLoading, user: currentUser, logout, refreshTokens } = useAuth()

  // Fetch companies
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await apiService.getCompanies()
      return Array.isArray(response) ? response : (response?.data || [])
    },
    enabled: isAuthenticated && !authLoading
  })

  const companies = Array.isArray(companiesData) ? companiesData : []

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (userData: typeof userForm & { companyId?: string; companyRole?: string }) => {
      return await apiService.createUser({
        name: userData.name,
        email: userData.email,
        role: userData.role,
        password: userData.password,
        companyId: userData.companyId,
        companyIds: userData.companyIds,
        companyRole: userData.companyRole
      })
    },
    onSuccess: async (newUser: any) => {
      // Assign user to additional companies with their roles
      const remainingCompanyIds = userForm.companyIds.slice(1)
      for (const companyId of remainingCompanyIds) {
        const companyRole = companyRoles[companyId] || userForm.role
        try {
          await apiService.inviteUserToCompany(companyId, {
            email: userForm.email,
            role: companyRole
          })
        } catch (error) {
          console.error(`Failed to assign user to company ${companyId}:`, error)
        }
      }
      
      toast({
        title: tSync("Success"),
        description: tSync("User created successfully"),
      })
      setShowUserModal(false)
      setEditingUser(null)
      setUserForm({ name: '', email: '', role: 'employee', password: '', companyIds: [] })
      setUserCompanies([])
      setCompanyRoles({})
      loadUsers()
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      toast({
        title: tSync("Error"),
        description: error?.message || tSync("Failed to create user. Please try again."),
        variant: "destructive",
      })
    }
  })

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async (userData: { userId: string; name: string; email: string; role: string; password?: string; companyIds: string[] }) => {
      return await apiService.updateUser(userData.userId, {
        name: userData.name,
        email: userData.email,
        role: userData.role,
        password: userData.password,
        companyIds: userData.companyIds
      })
    },
    onSuccess: () => {
      toast({
        title: tSync("Success"),
        description: tSync("User updated successfully"),
      })
      setShowUserModal(false)
      setEditingUser(null)
      setUserForm({ name: '', email: '', role: 'employee', password: '', companyIds: [] })
      setUserCompanies([])
      loadUsers()
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      toast({
        title: tSync("Error"),
        description: error?.message || tSync("Failed to update user. Please try again."),
        variant: "destructive",
      })
    }
  })

  const handleSubmitUser = async () => {
    // If editing existing user
    if (editingUser) {
      if (!userForm.name || !userForm.email) {
        toast({
          title: tSync("Validation Error"),
          description: tSync("Please fill in all required fields"),
          variant: "destructive",
        })
        return
      }

      try {
        // IMPORTANT: Don't pass companyIds to updateUser because it will delete and recreate
        // UserCompany relationships with the global role, overwriting company-specific roles.
        // Instead, update the user first (without company assignments), then handle company roles separately.
        
        // Update user first (without company assignments)
        await updateUserMutation.mutateAsync({
          userId: editingUser.id,
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          password: userForm.password || undefined,
          // Don't pass companyIds here - we'll handle company roles separately
          companyIds: undefined
        })
        
        // Get current user's companies to see which ones to remove
        const currentUserCompanies = await apiService.get(`/api/auth/users/${editingUser.id}/companies`) as any
        const currentCompanyIds = Array.isArray(currentUserCompanies) 
          ? currentUserCompanies.map((uc: any) => uc.companyId || uc.id || uc.company?.id).filter(Boolean)
          : (currentUserCompanies?.data || []).map((uc: any) => uc.companyId || uc.id || uc.company?.id).filter(Boolean)
        
        // Remove user from companies that are no longer selected
        const companiesToRemove = currentCompanyIds.filter((cid: string) => !userForm.companyIds.includes(cid))
        for (const companyId of companiesToRemove) {
          try {
            await apiService.removeUserFromCompany(companyId, editingUser.id)
          } catch (error) {
            console.error(`Failed to remove user from company ${companyId}:`, error)
          }
        }
        
        // Update or create company roles for selected companies
        for (const companyId of userForm.companyIds) {
          const companyRole = companyRoles[companyId] || 'employee'
          console.log(`[DEBUG] Updating company role for user ${editingUser.id} in company ${companyId} to role: ${companyRole}`)
          
          try {
            // Try to update the role directly first (this will work if user already has access)
            // If it fails with 404, then user doesn't have access and we'll invite them
            try {
              console.log(`[DEBUG] Attempting to update existing company role to ${companyRole}`)
              const updateResult = await apiService.updateUserCompanyRole(companyId, editingUser.id, companyRole)
              console.log(`[DEBUG] Update result:`, updateResult)
              
              // If update succeeded, verify it worked (only if we have access to check)
              if (currentUser?.role === 'admin') {
                try {
                  const verifyUsers = await apiService.getCompanyUsers(companyId)
                  const verifyUser = verifyUsers.find((u: any) => u.userId === editingUser.id)
                  console.log(`[DEBUG] Verified role after update:`, verifyUser?.companyRole)
                  
                  if (verifyUser?.companyRole !== companyRole) {
                    console.error(`[ERROR] Role update failed! Expected ${companyRole}, got ${verifyUser?.companyRole}`)
                    toast({
                      title: tSync("Warning"),
                      description: `Role update may have failed. Expected ${companyRole}, got ${verifyUser?.companyRole}. Please refresh and check.`,
                      variant: "destructive",
                    })
                  } else {
                    console.log(`[SUCCESS] Role updated successfully to ${companyRole}`)
                  }
                } catch (verifyError) {
                  // Can't verify, but update call succeeded so assume it worked
                  console.log(`[DEBUG] Update succeeded but couldn't verify (may not have access to check)`)
                }
              }
            } catch (updateError: any) {
              // If update fails with 404, user doesn't have access - invite them
              if (updateError?.status === 404 || updateError?.message?.includes('access_not_found') || updateError?.message?.includes('not have access')) {
                console.log(`[DEBUG] User doesn't have access (404), inviting with role ${companyRole}`)
                const inviteResult = await apiService.inviteUserToCompany(companyId, {
                  email: userForm.email,
                  role: companyRole
                })
                console.log(`[DEBUG] Invite result:`, inviteResult)
              } else {
                // Some other error occurred
                throw updateError
              }
            }
          } catch (error: any) {
            console.error(`[ERROR] Failed to update role for company ${companyId}:`, error)
            console.error(`[ERROR] Error details:`, {
              message: error?.message,
              response: error?.response,
              status: error?.status,
              data: error?.data
            })
            toast({
              title: tSync("Warning"),
              description: `Failed to update role for company: ${error?.message || 'Unknown error'}`,
              variant: "destructive",
            })
          }
        }
        
        // Check if we updated the current logged-in user's role
        const isCurrentUser = currentUser?.id === editingUser.id
        const roleChanged = editingUser.role !== userForm.role
        const isNowAdmin = userForm.role === 'admin'
        
        toast({
          title: tSync("Success"),
          description: tSync("User and company roles updated successfully"),
        })
        
        // If current user's role was changed, they need to re-login to get new permissions
        if (isCurrentUser && roleChanged) {
          setTimeout(() => {
            toast({
              title: tSync("Important"),
              description: isNowAdmin 
                ? tSync("Your role has been updated to admin. Please log out and log back in to access admin features.")
                : tSync("Your role has been updated. Please log out and log back in for changes to take effect."),
              duration: 10000,
            })
          }, 1000)
        }
        
        setShowUserModal(false)
        setEditingUser(null)
        setUserForm({ name: '', email: '', role: 'employee', password: '', companyIds: [] })
        setUserCompanies([])
        setCompanyRoles({})
        loadUsers()
        
        // Invalidate all related queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['users'] })
        queryClient.invalidateQueries({ queryKey: ['companyUsers'] })
        queryClient.invalidateQueries({ queryKey: ['companyAccessPermissions'] })
        
        // If we updated company roles, also invalidate company-specific queries
        for (const companyId of userForm.companyIds) {
          queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] })
          queryClient.invalidateQueries({ queryKey: ['companyAccessPermissions', companyId] })
        }
        
        // Try to refresh token if it's the current user (might not work if role changed significantly)
        if (isCurrentUser && !roleChanged) {
          try {
            await refreshTokens()
          } catch (error) {
            // Token refresh failed, user will need to re-login
            console.log('Token refresh failed, user should re-login')
          }
        }
      } catch (error: any) {
        toast({
          title: tSync("Error"),
          description: error?.message || tSync("Failed to update user. Please try again."),
          variant: "destructive",
        })
      }
      return
    }

      // If creating new user
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast({
        title: tSync("Validation Error"),
        description: tSync("Please fill in all required fields"),
        variant: "destructive",
      })
      return
    }

    if (userForm.companyIds.length === 0) {
      toast({
        title: tSync("Validation Error"),
        description: tSync("Please select at least one company"),
        variant: "destructive",
      })
      return
    }

    // Create user with company roles
    // The first company will be assigned during creation, others in onSuccess
    createUserMutation.mutate({
      ...userForm,
      companyId: userForm.companyIds[0], // First company for initial assignment
      companyRole: companyRoles[userForm.companyIds[0]] || userForm.role
    })
    
    // After user is created, assign to other companies with their roles
    // This will be handled in the onSuccess callback
  }

  const handleEditUser = async (user: User) => {
    setEditingUser(user)
    setUserForm({
      name: user.name || '',
      email: user.email,
      role: user.role,
      password: '',
      companyIds: []
    })
    
    // Fetch user's companies with their roles
    try {
      const response = await apiService.get(`/api/auth/users/${user.id}/companies`) as any
      const companies = Array.isArray(response) ? response : (response?.data || [])
      const companyIds = companies.map((uc: any) => uc.companyId || uc.id || uc.company?.id).filter(Boolean)
      setUserCompanies(companyIds)
      setUserForm(prev => ({ ...prev, companyIds }))
      
      // Store company roles: { companyId: role }
      const rolesMap: Record<string, string> = {}
      companies.forEach((uc: any) => {
        const cid = uc.companyId || uc.id || uc.company?.id
        if (cid) {
          rolesMap[cid] = uc.role || 'employee'
        }
      })
      setCompanyRoles(rolesMap)
    } catch (error) {
      console.error('Failed to fetch user companies:', error)
      setUserCompanies([])
      setCompanyRoles({})
    }
    
    setShowUserModal(true)
  }

  const handleAddUser = () => {
    setEditingUser(null)
    setUserForm({ name: '', email: '', role: 'accountant', password: '', companyIds: [] })
    setUserCompanies([])
    setCompanyRoles({})
    setShowUserModal(true)
  }

  const handleCompanyToggle = (companyId: string) => {
    setUserForm(prev => {
      const companyIds = prev.companyIds.includes(companyId)
        ? prev.companyIds.filter(id => id !== companyId)
        : [...prev.companyIds, companyId]
      
      // If removing company, remove its role too
      if (!companyIds.includes(companyId)) {
        setCompanyRoles(prev => {
          const newRoles = { ...prev }
          delete newRoles[companyId]
          return newRoles
        })
      } else {
        // If adding company, set default role
        setCompanyRoles(prev => ({
          ...prev,
          [companyId]: prev[companyId] || 'employee'
        }))
      }
      
      return { ...prev, companyIds }
    })
  }

  const handleCompanyRoleChange = (companyId: string, role: string) => {
    setCompanyRoles(prev => ({
      ...prev,
      [companyId]: role
    }))
  }

  const loadUsers = async () => {
    setLoading(true)
    try {
      const data = await apiService.getUsers()
      const usersList = data.users || []
      
      // Fetch company roles for each user
      const usersWithCompanyRoles = await Promise.all(
        usersList.map(async (user: User) => {
          try {
            const userCompaniesResponse = await apiService.get(`/api/auth/users/${user.id}/companies`) as any
            const userCompanies = Array.isArray(userCompaniesResponse) 
              ? userCompaniesResponse 
              : (userCompaniesResponse?.data || [])
            
            const companyIds = userCompanies.map((uc: any) => uc.companyId || uc.id || uc.company?.id).filter(Boolean)
            
            // Fetch roles for each company
            const roles: Record<string, string> = {}
            for (const companyId of companyIds) {
              try {
                const companyUsers = await apiService.getCompanyUsers(companyId)
                const companyUser = companyUsers.find((u: any) => u.userId === user.id)
                if (companyUser) {
                  roles[companyId] = companyUser.companyRole || 'accountant'
                }
              } catch (error) {
                // Skip if can't fetch
              }
            }
            
            return { ...user, companyRoles: roles }
          } catch (error) {
            return { ...user, companyRoles: {} }
          }
        })
      )
      
      setUsers(usersWithCompanyRoles)
    } catch (error) {
      console.error('Error loading users:', error)
      toast({
        title: tSync("Error"),
        description: tSync("Failed to load users. Please try again."),
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadUsers()
    }
  }, [isAuthenticated, authLoading])

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800'
      case 'accountant': return 'bg-blue-100 text-blue-800'
      case 'auditor': return 'bg-purple-100 text-purple-800'
      case 'employee': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    }
    return email[0].toUpperCase()
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight"><TranslatedText text="User Management" /></h1>
            <p className="text-muted-foreground">
              <TranslatedText text="Manage users, roles, and access permissions" />
            </p>
          </div>
          <Button onClick={handleAddUser}>
            <UserPlus className="mr-2 h-4 w-4" />
            <TranslatedText text="Add User" />
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Total Users" /></CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.length}</div>
              <p className="text-xs text-muted-foreground">
                <TranslatedText text="Active users in the system" />
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="MFA Enabled" /></CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {users.filter(user => user.mfaEnabled).length}
              </div>
              <p className="text-xs text-muted-foreground">
                <TranslatedText text="Users with 2FA protection" />
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Admins" /></CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {users.filter(user => user.role === 'admin').length}
              </div>
              <p className="text-xs text-muted-foreground">
                <TranslatedText text="Administrative users" />
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle><TranslatedText text="All Users" /></CardTitle>
            <CardDescription>
              <TranslatedText text="View and manage all users in your organization" />
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-muted-foreground"><TranslatedText text="Loading users..." /></div>
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold"><TranslatedText text="No users found" /></h3>
                <p className="text-muted-foreground">
                  <TranslatedText text="Get started by adding your first user" />
                </p>
                <Button className="mt-4" onClick={handleAddUser}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  <TranslatedText text="Add User" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>
                          {getInitials(user.name, user.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold">
                            {user.name || <TranslatedText text="No name set" />}
                          </h3>
                          <Badge className={getRoleColor(user.role)}>
                            {user.role === 'admin' ? <TranslatedText text="Admin" /> : 
                             user.role === 'accountant' ? <TranslatedText text="Accountant" /> :
                             user.role === 'auditor' ? <TranslatedText text="Auditor" /> :
                             user.role === 'employee' ? <TranslatedText text="Employee" /> :
                             user.role}
                          </Badge>
                          {user.companyRoles && Object.keys(user.companyRoles).length > 0 && (
                            <Badge variant="outline" className="text-blue-600 border-blue-600">
                              {Object.values(user.companyRoles).includes('admin') ? 'Company Admin' : 
                               Object.values(user.companyRoles).includes('accountant') ? 'Company Accountant' :
                               'Company User'}
                            </Badge>
                          )}
                          {user.mfaEnabled && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              <CheckCircle className="mr-1 h-3 w-3" />
                              2FA
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <div className="flex items-center">
                            <Mail className="mr-1 h-3 w-3" />
                            {user.email}
                          </div>
                          <div className="flex items-center">
                            <Calendar className="mr-1 h-3 w-3" />
                            <TranslatedText text="Joined" /> {formatDate(user.createdAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditUser(user)}>
                        <TranslatedText text="Edit" />
                      </Button>
                      <Button variant="outline" size="sm">
                        <TranslatedText text="Reset Password" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Unified User Modal (Create/Edit) */}
      <Dialog open={showUserModal} onOpenChange={(open) => {
        setShowUserModal(open)
        if (!open) {
          setEditingUser(null)
          setUserForm({ name: '', email: '', role: 'employee', password: '', companyIds: [] })
          setUserCompanies([])
        }
      }}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-3">
            <DialogTitle className="text-lg">
              {editingUser ? <TranslatedText text="Edit User" /> : <TranslatedText text="Add New User" />}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {editingUser 
                ? <TranslatedText text="Update user information and company assignments" />
                : <TranslatedText text="Create a new user account and assign companies" />
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm"><TranslatedText text="Full Name" /> *</Label>
                <Input
                  id="name"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  placeholder={tSync("Enter full name")}
                  className="h-9 text-sm border"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm"><TranslatedText text="Email" /> *</Label>
                <Input
                  id="email"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder={tSync("Enter email")}
                  className="h-9 text-sm border"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="role" className="text-sm">
                  <TranslatedText text="Global Role" /> (System-Wide) *
                  <span className="text-xs text-muted-foreground ml-1">
                    (Only "admin" here = System Admin)
                  </span>
                </Label>
                <Select value={userForm.role} onValueChange={(value) => setUserForm({ ...userForm, role: value })}>
                  <SelectTrigger className="h-9 text-sm border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accountant"><TranslatedText text="Accountant" /></SelectItem>
                    <SelectItem value="admin"><TranslatedText text="Admin" /> (System Admin)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {editingUser?.role === 'accountant' && (
                    <span className="text-blue-600">
                      💡 To make this accountant a company admin, set their Company Role to "admin" below (not here).
                    </span>
                  )}
                  {editingUser?.role !== 'accountant' && (
                    <span>Global role applies system-wide. Set company roles below.</span>
                  )}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm">
                  <TranslatedText text="Password" /> {editingUser ? '' : '*'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder={editingUser ? tSync("Leave blank to keep current password") : tSync("Enter password")}
                  className="h-9 text-sm border"
                />
                {editingUser && (
                  <p className="text-xs text-muted-foreground">
                    <TranslatedText text="Leave blank to keep the current password" />
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm"><TranslatedText text="Companies & Roles" /> *</Label>
              <div className="border rounded-md p-2 max-h-60 overflow-y-auto bg-muted/30">
                {companies.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 text-center"><TranslatedText text="No companies available" /></p>
                ) : (
                  <div className="space-y-2">
                    {companies.map((company: any) => {
                      const isSelected = userForm.companyIds.includes(company.id)
                      const companyRole = companyRoles[company.id] || 'accountant'
                      return (
                        <div key={company.id} className="flex flex-col gap-2 p-2 hover:bg-background/50 rounded border border-transparent hover:border-border">
                          <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`company-${company.id}`}
                              checked={isSelected}
                          onChange={() => handleCompanyToggle(company.id)}
                          className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        />
                            <label htmlFor={`company-${company.id}`} className="text-sm font-medium cursor-pointer flex-1">
                          {company.name}
                        </label>
                      </div>
                          {isSelected && (
                            <div className="ml-6 space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                <TranslatedText text="Company Role" />:
                                <span className="text-xs text-blue-600 ml-1">
                                  {editingUser?.role === 'accountant' && companyRole !== 'admin' 
                                    ? '💡 Make "admin" to give company admin access'
                                    : companyRole === 'admin'
                                      ? ' (Company Admin)'
                                      : ''
                                  }
                                </span>
                              </Label>
                              <Select
                                value={companyRole}
                                onValueChange={(value) => handleCompanyRoleChange(company.id, value)}
                              >
                                <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                                  <SelectItem value="admin">
                                    <div>
                                      <div className="font-medium">Admin</div>
                                      <div className="text-xs text-muted-foreground">Company Admin (can manage users)</div>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="accountant">
                                    <div>
                                      <div className="font-medium">Accountant</div>
                                      <div className="text-xs text-muted-foreground">Can manage finances</div>
                                    </div>
                                  </SelectItem>
                  </SelectContent>
                </Select>
              </div>
                          )}
              </div>
                      )
                    })}
                  </div>
                )}
              </div>
              {userForm.companyIds.length === 0 && (
                <p className="text-xs text-destructive mt-1"><TranslatedText text="Please select at least one company" /></p>
              )}
              {userForm.companyIds.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {userForm.companyIds.length} {userForm.companyIds.length === 1 ? <TranslatedText text="company selected" /> : <TranslatedText text="companies selected" />}
                  {editingUser?.role === 'accountant' && Object.values(companyRoles).includes('admin') && (
                    <span className="text-blue-600 ml-2">
                      • <TranslatedText text="This accountant will be a company admin for selected companies" />
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="pt-3 border-t">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setShowUserModal(false)
                setEditingUser(null)
                setUserForm({ name: '', email: '', role: 'employee', password: '', companyIds: [] })
                setUserCompanies([])
                setCompanyRoles({})
              }}
            >
              <TranslatedText text="Cancel" />
            </Button>
            <Button 
              size="sm"
              onClick={handleSubmitUser} 
              disabled={editingUser ? updateUserMutation.isPending : createUserMutation.isPending}
            >
              {editingUser 
                ? (updateUserMutation.isPending ? <TranslatedText text="Updating..." /> : <TranslatedText text="Update User" />)
                : (createUserMutation.isPending ? <TranslatedText text="Creating..." /> : <TranslatedText text="Create User" />)
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  )
}
